import React, { useState, useEffect, useMemo } from "react";
import { Routes, Route, Switch, withRouter } from "react-router-dom";
import request from "request"

import formatCurrency from "utils/formatCurrency"

import { SkewLoader } from "react-spinners"
import { xor } from "lodash"

import Icon from "Icon"
import Input from "Input"
import Modal from "Modal"
import Button from "Button"

const ProductView = ({addProduct, isModifyingOrder, selectedProduct, clearProduct}) => {
  const [ product, setProduct ] = useState(null)
  const [ isLoading, setIsLoading ] = useState(false)
  const [ quantity, setQuantity ] = useState(1)
  const [ selectedModifiers, setSelectedModifiers ] = useState([])

  const fetchProduct = () => {
    if (!selectedProduct) return false

    setIsLoading(true)
    const req = request.get(`store_products/${selectedProduct}`, { params: { view: "terminal" }})
    req.then(({data}) => {
      setProduct(data.product)
      setIsLoading(false)
    })
  }

  useEffect(fetchProduct, [selectedProduct])

  const closeModal = () => {
    setQuantity(1)
    setProduct(null)
    clearProduct()
    setSelectedModifiers([])
  }

  const modifierGroups = (product && (product.modifierGroups || product.modifier_groups)) || []

  // IDs that belong to some group, used to identify ungrouped modifiers
  const groupedModifierIds = useMemo(() => {
    const ids = new Set()
    modifierGroups.forEach((g) => (g.modifiers || []).forEach((m) => ids.add(m.id)))
    return ids
  }, [product])

  const ungroupedModifiers = useMemo(() => {
    if (!product || !product.modifiers) return []
    return product.modifiers.filter((m) => !groupedModifierIds.has(m.id))
  }, [product, groupedModifierIds])

  const selectionsForGroup = (group) => {
    const groupIds = new Set((group.modifiers || []).map((m) => m.id))
    return selectedModifiers.filter((id) => groupIds.has(id))
  }

  const groupErrors = useMemo(() => {
    const errors = {}
    modifierGroups.forEach((group) => {
      const count = selectionsForGroup(group).length
      if (group.required && count < 1) {
        errors[group.id] = "Please select at least one option"
      } else if (group.max && count > group.max) {
        errors[group.id] = `Select no more than ${group.max}`
      }
    })
    return errors
  }, [modifierGroups, selectedModifiers])

  const hasErrors = Object.keys(groupErrors).length > 0

  const toggleModifier = (modifierId, group = null) => {
    const isSelected = selectedModifiers.indexOf(modifierId) > -1

    // Adding a modifier to a group already at its max: evict the oldest
    // selection from the same group so the user's latest pick takes effect.
    if (!isSelected && group && group.max) {
      const groupSelections = selectionsForGroup(group)
      if (groupSelections.length >= group.max) {
        const toRemove = groupSelections.slice(0, groupSelections.length - group.max + 1)
        const next = selectedModifiers.filter((id) => !toRemove.includes(id))
        next.push(modifierId)
        setSelectedModifiers(next)
        return
      }
    }

    setSelectedModifiers(xor(selectedModifiers, [ modifierId ]))
  }

  const addToOrder = () => {
    if(!product) return false
    if(hasErrors) return false

    addProduct(selectedProduct, quantity, selectedModifiers)
    closeModal()
  }

  const renderModalActions = () => {
    return(
      <div className="is-flex is-flex-grow-1">
        <Button
          onClick={closeModal}
          text="Cancel"
          className="is-outlined"
          color="red"
        />

        <div className="mx-auto is-flex is-align-items-center">
          <Button disabled={quantity == 1} onClick={() => setQuantity(quantity - 1)} text="-"/>
          <span className="tag is-size-6 has-text-weight-bold mr-2">{ quantity }</span>
          <Button onClick={() => setQuantity(quantity + 1)} text="+"/>
        </div>

        <div>
          <Button
            text="Add to Order"
            loading={isModifyingOrder}
            disabled={!(product && product.isActive) || hasErrors}
            onClick={addToOrder}
            color="green"
            className="is-text-weight-bold"
          />
        </div>
      </div>
    )
  }

  const renderModifierRow = (modifier, group = null) => {
    const isSelected = selectedModifiers.indexOf(modifier.id) > -1
    return(
      <div
        key={modifier.id}
        className="ml-2 my-1 columns is-mobile is-justify-content-center"
        onClick={() => toggleModifier(modifier.id, group)}
        style={{ cursor: "pointer" }}
      >
        <div className="column is-narrow">
          <Icon className="is-size-4 m-0" icon={isSelected ? "check-square" : "square" } />
        </div>
        <div className="column">
          <p className="is-size-5">{ modifier.name }</p>
        </div>
        <div className="column is-narrow">
          { modifier.price != 0 &&
            (parseFloat(modifier.price) >= 0 ? "+" : "-") + "$" + modifier.price
          }
        </div>
      </div>
    )
  }

  const renderGroupHint = (group) => {
    const hints = []
    if (group.required) hints.push("Required")
    if (group.max) hints.push(`pick up to ${group.max}`)
    if (hints.length === 0) return null
    return <span className="is-size-7 has-text-grey ml-2">({ hints.join(", ") })</span>
  }

  const renderModifierGroup = (group) => {
    const error = groupErrors[group.id]
    return (
      <div
        key={group.id}
        className="mt-4 pt-3"
        style={{ borderTop: "1px solid #e6e6e6" }}
      >
        <div className="is-flex is-align-items-center is-justify-content-space-between">
          <h5 className="is-size-5 has-text-weight-semibold mb-0">
            { group.name }
            { renderGroupHint(group) }
          </h5>
          { group.required &&
            <span className="tag is-warning is-light">Required</span>
          }
        </div>
        { error &&
          <p className="has-text-danger is-size-7 mt-1">{ error }</p>
        }
        <div className="mt-2">
          { (group.modifiers || []).map((m) => renderModifierRow(m, group)) }
        </div>
      </div>
    )
  }

  return(
    <div className="ProductView">
      <Modal
        open={!!selectedProduct}
        toggleModal={closeModal}
        actions={renderModalActions()}
      >
        { !product &&
          <div className="is-flex is-justify-content-center p-4">
            <SkewLoader size={40} color="#F9690E" />
          </div>
        }
        { product &&
          <div>
            <h3 className="title">
              <span style={{}} className={`mr-2 p-2 ${product.isActive ? "has-background-success" : "has-background-danger"}`} />
              { product.name }
            </h3>
            <h4 className="subtitle mt-2">
              Price {formatCurrency(product.price)}
            </h4>

            { modifierGroups.map(renderModifierGroup) }

            { ungroupedModifiers.length > 0 &&
              <div
                className={modifierGroups.length > 0 ? "mt-4 pt-3" : "mt-2"}
                style={modifierGroups.length > 0 ? { borderTop: "1px solid #e6e6e6" } : {}}
              >
                { modifierGroups.length > 0 &&
                  <h5 className="is-size-5 has-text-weight-semibold mb-2">Other</h5>
                }
                { ungroupedModifiers.map((m) => renderModifierRow(m)) }
              </div>
            }
          </div>
        }
      </Modal>
    </div>
  )
}

export default ProductView
